/**
 * Markdown Rendering Extension
 *
 * Provides blur→render / focus→source markdown rendering for CodeMirror.
 *
 * Usage:
 * ```javascript
 * import { markdown } from 'mrmd-editor/markdown';
 *
 * const view = new EditorView({
 *   extensions: [
 *     markdown(),
 *     // ... other extensions
 *   ],
 * });
 * ```
 *
 * The rendering is opinionated and follows MRMD's design:
 * - Markers (# ** __) hidden on blur, muted on focus
 * - Block elements (tables, images) use stable layout pattern
 * - No jitter when cursor moves
 *
 * Theming is done via CSS variables (--md-*) from the theme system.
 * See widgets/theme.js for available tokens.
 *
 * @module markdown
 */

import { markdownRenderer } from './renderer.js';
import { blockDecorations, lineHeightTracker } from './block-decorations.js';
import { markdownStyles, injectMarkdownStyles } from './styles.js';

/**
 * Create the markdown rendering extension.
 *
 * Architecture:
 * - blockDecorations (StateField): Tables, display math - multi-line Decoration.replace
 * - markdownRenderer (ViewPlugin): Everything else - single-line decorations
 *
 * This split is required because CodeMirror only allows multi-line replacing
 * decorations from StateFields, not ViewPlugins.
 *
 * @returns {import('@codemirror/state').Extension}
 */
export function markdown() {
  // Inject styles on first use
  injectMarkdownStyles();

  console.log('[Markdown] Creating extensions, blockDecorations:', blockDecorations, 'markdownRenderer:', markdownRenderer);

  return [
    lineHeightTracker,   // ViewPlugin: updates line height cache (must come first!)
    blockDecorations,    // StateField: tables, display math
    markdownRenderer,    // ViewPlugin: everything else
  ];
}

// Export individual pieces for advanced use
export { markdownRenderer, assetResolverFacet } from './renderer.js';
export { blockDecorations, lineHeightTracker, cacheWidgetHeight, getCachedHeight, clearHeightCache } from './block-decorations.js';
export { markdownStyles, injectMarkdownStyles } from './styles.js';

// Widget exports
export {
  TaskCheckboxWidget,
  ImageWidget,
  ImagePlaceholder,
  parseImageMarkdown,
  TableWidget,
  parseTable,
  isTableLine,
  isTableDelimiter,
  generateTableId,
  AlertTitleWidget,
  // Math widgets
  InlineMathWidget,
  DisplayMathWidget,
  MathPlaceholder,
  hasInlineMath,
  isDisplayMath,
  extractInlineMath,
  extractDisplayMath,
  generateMathId,
  isKaTeXAvailable,
  renderLatex,
  defaultMacros,
  getKaTeXVersion,
  injectKaTeXStyles,
} from './widgets/index.js';

// HTML inline rendering
export {
  containsHtml,
  extractHtmlElements,
  renderTextWithHtml,
  renderInlineMarkdownWithHtml,
  createHtmlElement,
  InlineHtmlWidget,
} from './html-inline.js';
